import type { BibleCharacter, BibleCostume, BibleLocation, BibleProp, BibleStyle } from '@/api/studio-production'
import { canvasPath } from '@/features/studio/lib/canvas-path'

export type BibleEntryKind = 'character' | 'location' | 'prop' | 'costume' | 'style'

export interface BibleEntryLike {
  public_id: string
  name: string
  description: string
  reference_urls?: string[]
  reference_url?: string
}

export type BibleListItem = BibleCharacter | BibleLocation | BibleProp | BibleCostume

export function bibleAllowsMultipleImages(kind: BibleEntryKind): boolean {
  return kind === 'character'
}

export function bibleShowsSeriesOutline(kind: BibleEntryKind): boolean {
  return kind === 'character'
}

export function bibleKindLabelKey(kind: BibleEntryKind): string {
  if (kind === 'character') return 'bible.kindCharacter'
  if (kind === 'location') return 'bible.kindLocation'
  if (kind === 'prop') return 'bible.kindProp'
  if (kind === 'costume') return 'bible.kindCostume'
  return 'bible.tab.style'
}

export function bibleReferenceUrls(item: {
  reference_url?: string
  reference_urls?: string[]
}): string[] {
  const fromList = (item.reference_urls ?? []).map((u) => u.trim()).filter(Boolean)
  if (fromList.length > 0) return fromList
  const legacy = item.reference_url?.trim()
  return legacy ? [legacy] : []
}

export function toBibleEntry(item: BibleListItem): BibleEntryLike {
  return {
    public_id: item.public_id,
    name: item.name,
    description: item.description,
    reference_url: item.reference_url,
    reference_urls: bibleReferenceUrls(item),
  }
}

export type BibleStyleField = 'art_direction' | 'color_palette' | 'style_prompt' | 'negative_prompt'

export const BIBLE_STYLE_ENTRY_IDS: Record<BibleStyleField, string> = {
  art_direction: '__style_art__',
  color_palette: '__style_color__',
  style_prompt: '__style_prompt__',
  negative_prompt: '__style_negative__',
}

const BIBLE_STYLE_FIELDS: BibleStyleField[] = [
  'art_direction',
  'color_palette',
  'style_prompt',
  'negative_prompt',
]

export function styleFieldFromEntryId(publicId: string): BibleStyleField | null {
  for (const field of BIBLE_STYLE_FIELDS) {
    if (BIBLE_STYLE_ENTRY_IDS[field] === publicId) return field
  }
  if (publicId === '__style__') return 'art_direction'
  return null
}

export function styleFieldNameKey(field: BibleStyleField): string {
  if (field === 'art_direction') return 'bible.style.artDirection'
  if (field === 'color_palette') return 'bible.style.colorPalette'
  if (field === 'style_prompt') return 'bible.style.stylePrompt'
  return 'bible.style.negativePrompt'
}

export function styleFieldPlaceholderKey(field: BibleStyleField): string {
  return `${styleFieldNameKey(field)}Placeholder`
}

export function firstEmptyStyleField(style: BibleStyle | null | undefined): BibleStyleField | null {
  if (!style) return 'art_direction'
  for (const field of BIBLE_STYLE_FIELDS) {
    if (field === 'art_direction') {
      if (!style.art_direction.trim() && bibleReferenceUrls(style).length === 0) return field
      continue
    }
    if (!style[field].trim()) return field
  }
  return null
}

export function styleToEntries(
  style: BibleStyle | null | undefined,
  t: (key: string) => string,
): BibleEntryLike[] {
  if (!style) return []
  return BIBLE_STYLE_FIELDS.flatMap((field) => {
    const description = style[field].trim()
    const reference_urls = field === 'art_direction' ? bibleReferenceUrls(style) : []
    if (!description && reference_urls.length === 0) return []
    return [
      {
        public_id: BIBLE_STYLE_ENTRY_IDS[field],
        name: t(styleFieldNameKey(field)),
        description,
        reference_urls,
      },
    ]
  })
}

/** @deprecated 使用 styleToEntries */
export function styleToEntry(style: BibleStyle, styleName: string): BibleEntryLike | null {
  const urls = bibleReferenceUrls(style)
  const description = styleSummaryText(style)
  if (!description && urls.length === 0) return null
  return {
    public_id: '__style__',
    name: styleName,
    description,
    reference_urls: urls,
  }
}

export function styleSummaryText(style: BibleStyle): string {
  return [style.art_direction, style.color_palette, style.style_prompt, style.negative_prompt]
    .map((s) => s.trim())
    .filter(Boolean)
    .join('\n\n')
}

export function canvasCtxForKind(kind: BibleEntryKind): string {
  if (kind === 'character') return 'bible-character'
  if (kind === 'location') return 'bible-location'
  if (kind === 'prop') return 'bible-prop'
  if (kind === 'costume') return 'bible-costume'
  return 'bible-style'
}

export function createCanvasLabelKey(kind: BibleEntryKind): string {
  if (kind === 'character') return 'bible.createCharacter'
  if (kind === 'location') return 'bible.createLocation'
  if (kind === 'prop') return 'bible.createProp'
  if (kind === 'costume') return 'bible.createCostume'
  return 'bible.createStyle'
}

export function deleteConfirmKey(kind: BibleEntryKind): string {
  if (kind === 'character') return 'bible.deleteCharacterConfirm'
  if (kind === 'location') return 'bible.deleteLocationConfirm'
  if (kind === 'prop') return 'bible.deletePropConfirm'
  if (kind === 'costume') return 'bible.deleteCostumeConfirm'
  return 'bible.deleteStyleConfirm'
}

export function uploadAssetKindTag(kind: BibleEntryKind): string {
  if (kind === 'character') return 'character_ref'
  if (kind === 'location') return 'location_ref'
  if (kind === 'prop') return 'prop'
  if (kind === 'costume') return 'misc'
  return 'misc'
}

export function bibleRefTypeForKind(kind: BibleEntryKind): string {
  if (kind === 'character') return 'character'
  if (kind === 'location') return 'location'
  if (kind === 'prop') return 'prop'
  if (kind === 'costume') return 'costume'
  return 'style'
}

export function bibleAssignmentItemTypeForKind(kind: BibleEntryKind): string {
  if (kind === 'character') return 'bible_character'
  if (kind === 'location') return 'bible_location'
  if (kind === 'prop') return 'bible_prop'
  if (kind === 'costume') return 'bible_costume'
  return 'bible_style'
}

export function bibleRefPublicIdForEntry(kind: BibleEntryKind, publicId: string): string {
  if (kind === 'style') return 'default'
  return publicId
}

/** 美术设定卡片 → 画布链接；缺少 public_id 时返回 null（避免 render 抛错） */
export function bibleCanvasPath(
  productionId: string,
  kind: BibleEntryKind,
  entry: Pick<BibleEntryLike, 'public_id' | 'name'>,
  returnPath: string,
): string | null {
  const ctx = canvasCtxForKind(kind)
  if (ctx === 'bible-style') {
    return canvasPath(productionId, { ctx, label: entry.name, return: returnPath })
  }
  const entityId = entry.public_id?.trim()
  if (!entityId || entityId === '__style__') return null
  return canvasPath(productionId, { ctx, entityId, label: entry.name, return: returnPath })
}

export function descriptionPlaceholderKey(kind: BibleEntryKind): string {
  if (kind === 'character') return 'bible.fieldDescriptionPlaceholder'
  if (kind === 'style') return 'bible.style.artDirectionPlaceholder'
  return 'bible.fieldLocationDescriptionPlaceholder'
}
